import { useCallback, useEffect, useState } from "react";

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_STORAGE_NAMESPACE = "garimpa.messagingPrompt.dismissedUntil";

export function useMessagingPromptDismissal(
  viewerScope: string | undefined,
  storageNamespace = DEFAULT_STORAGE_NAMESPACE,
) {
  const [state, setState] = useState<{ dismissed: boolean; viewerScope?: string }>({
    dismissed: true,
  });
  const storageKey = viewerScope ? `${storageNamespace}.${viewerScope}` : undefined;

  useEffect(() => {
    if (!storageKey || !viewerScope) return;
    let dismissed = true;
    try {
      const dismissedUntil = Number(window.localStorage.getItem(storageKey));
      dismissed = Number.isFinite(dismissedUntil) && dismissedUntil > Date.now();
    } catch {
      // If storage is unavailable, keep automatic prompts suppressed.
    }
    setState({
      dismissed,
      viewerScope,
    });
  }, [storageKey, viewerScope]);

  const dismissForDays = useCallback(
    (days: number) => {
      if (!storageKey || !viewerScope) return;
      try {
        window.localStorage.setItem(storageKey, String(Date.now() + days * MILLISECONDS_PER_DAY));
      } catch {
        // Session state still prevents another prompt when storage is unavailable.
      }
      setState({ dismissed: true, viewerScope });
    },
    [storageKey, viewerScope],
  );

  const clearDismissal = useCallback(() => {
    if (!storageKey || !viewerScope) return;
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // The in-memory state remains authoritative for this session.
    }
    setState({ dismissed: false, viewerScope });
  }, [storageKey, viewerScope]);

  return {
    dismissed: state.viewerScope === viewerScope ? state.dismissed : true,
    dismissForDays,
    clearDismissal,
    isResolved: Boolean(viewerScope) && state.viewerScope === viewerScope,
  };
}
